import { useCallback, useMemo, useRef, useState } from 'react';
import { compressImageFile } from '../../utils/compress';
import {
  ApiError,
  GENERIC_ERROR,
  deletePage,
  listPages,
  reorderPages,
  summariseQr,
  uploadPage,
} from './api';
import {
  ACCEPTED_MIME_TYPES,
  MAX_BYTES_PER_FILE,
  MAX_MEGABYTES_PER_FILE,
  MAX_PAGES_PER_DOCUMENT,
} from './constants';
import { DocumentPage, PageRow } from './types';

// The pages of a document on the server, with their upload queue:
// add (file or capture), retry, dismiss, delete, reorder.
//
// Extracted from useDraft so that the same management serves over an already
// archived document (the "Pages" tab of "Modifier le document", ROADMAP.md,
// Part 3, "Outside the wizard"): the wizard puts the draft lifecycle on top;
// the edit, on-demand assembly. Neither of the two duplicates the queue.
//
// Uploads are sequential even if the user drops several files at
// once: the backend assigns the positions in a transaction and, in series,
// the on-screen order is the order they were dropped in. Each in-progress row
// carries its progress; a failed upload stays in the list with its message, to
// retry it or dismiss it.
//
// `version` counts the changes accepted by the server (page added,
// deleted or moved). Whoever assembles the PDF compares with the version it had
// to know whether there is anything to regenerate.

// `name` is the name the user chose; `file` may be the compressed version.
type Queued = { key: string; file: File; name: string };

export const errorText = (err: unknown): string =>
  err instanceof ApiError ? err.message : GENERIC_ERROR;

let keySeed = 0;
const nextKey = () => `upload_${Date.now()}_${keySeed++}`;

// Pre-upload checks, with the same criteria as the backend.
// Returns the reason, or null if the file can be attempted.
export const refuseFile = (file: File): string | null => {
  if (!ACCEPTED_MIME_TYPES.includes(file.type.toLowerCase())) {
    return 'Format non pris en charge. Formats autorisés : PDF, PNG, JPEG.';
  }
  if (file.size > MAX_BYTES_PER_FILE) {
    return `Fichier trop volumineux : ${MAX_MEGABYTES_PER_FILE} Mo maximum.`;
  }
  return null;
};

const sortedRows = (fromServer: DocumentPage[]): PageRow[] =>
  fromServer
    .slice()
    .sort((a, b) => a.position - b.position)
    .map<PageRow>((page) => ({ kind: 'page', key: page.id, page }));

export const usePages = (documentId: string | undefined) => {
  const [rows, setRows] = useState<PageRow[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);

  const documentIdRef = useRef(documentId);
  const queueRef = useRef<Queued[]>([]);
  const filesRef = useRef(new Map<string, File>());
  const fileNamesRef = useRef(new Map<string, string>());
  const pumpingRef = useRef(false);
  const cancelledRef = useRef(false);
  // The in-flight upload, to cut it off if the document is discarded meanwhile.
  const abortRef = useRef<AbortController | null>(null);

  documentIdRef.current = documentId;

  // --- Rows -------------------------------------------------------------

  const pages = useMemo(
    () =>
      rows
        .filter((row): row is Extract<PageRow, { kind: 'page' }> => row.kind === 'page')
        .map((row) => row.page),
    [rows],
  );

  const uploadsInProgress = rows.some(
    (row) => row.kind === 'upload' && !row.error,
  );

  const qr = useMemo(() => summariseQr(pages), [pages]);

  const bump = useCallback(() => setVersion((current) => current + 1), []);

  // Rebuilds the rows from the server, keeping the pending uploads
  // and the file names from this session.
  const syncPages = useCallback((fromServer: DocumentPage[]) => {
    setRows((current) => [
      ...sortedRows(fromServer).map((row) =>
        row.kind === 'page'
          ? { ...row, fileName: fileNamesRef.current.get(row.page.id) }
          : row,
      ),
      ...current.filter((row) => row.kind === 'upload'),
    ]);
  }, []);

  // Starts from scratch with what the server has: no pending uploads, no
  // session names, and the queue reopened if it had been cancelled.
  const reset = useCallback((fromServer: DocumentPage[]) => {
    queueRef.current = [];
    filesRef.current.clear();
    fileNamesRef.current.clear();
    cancelledRef.current = false;
    setRows(sortedRows(fromServer));
    setNotice(null);
  }, []);

  const reloadPages = useCallback(async () => {
    const id = documentIdRef.current;
    if (!id) return;
    try {
      syncPages(await listPages(id));
    } catch (err) {
      setNotice(errorText(err));
    }
  }, [syncPages]);

  // --- Uploads ----------------------------------------------------------

  const patchRow = useCallback(
    (key: string, patch: (row: PageRow) => PageRow) => {
      setRows((current) =>
        current.map((row) => (row.key === key ? patch(row) : row)),
      );
    },
    [],
  );

  const pump = useCallback(async () => {
    if (pumpingRef.current) return;
    pumpingRef.current = true;

    try {
      while (queueRef.current.length > 0 && !cancelledRef.current) {
        const { key, file, name } = queueRef.current.shift() as Queued;
        const id = documentIdRef.current;
        if (!id) break;

        patchRow(key, (row) =>
          row.kind === 'upload' ? { ...row, progress: 0, error: undefined } : row,
        );

        const controller = new AbortController();
        abortRef.current = controller;

        try {
          const pages = await uploadPage(
            id,
            file,
            (fraction) => {
              patchRow(key, (row) =>
                row.kind === 'upload' ? { ...row, progress: fraction } : row,
              );
            },
            controller.signal,
          );
          pages.forEach((page) => fileNamesRef.current.set(page.id, name));
          filesRef.current.delete(key);
          setRows((current) =>
            current.flatMap<PageRow>((row) =>
              row.key === key
                ? pages.map<PageRow>((page) => ({
                    kind: 'page',
                    key: page.id,
                    page,
                    fileName: name,
                  }))
                : [row],
            ),
          );
          bump();
        } catch (err) {
          if (cancelledRef.current) break;
          patchRow(key, (row) =>
            row.kind === 'upload'
              ? { ...row, progress: null, error: errorText(err) }
              : row,
          );
        } finally {
          abortRef.current = null;
        }
      }
    } finally {
      pumpingRef.current = false;
    }
  }, [patchRow, bump]);

  const addFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      // Counts what is already there and what is on the way; the failed ones do not count.
      let taken = rows.filter(
        (row) => row.kind === 'page' || !row.error,
      ).length;
      const accepted: File[] = [];
      const refused: string[] = [];

      for (const file of files) {
        const reason = refuseFile(file);
        if (reason) {
          refused.push(`${file.name} : ${reason}`);
          continue;
        }
        if (taken >= MAX_PAGES_PER_DOCUMENT) {
          refused.push(
            `${file.name} : un document ne peut pas dépasser ${MAX_PAGES_PER_DOCUMENT} pages.`,
          );
          continue;
        }
        taken += 1;
        accepted.push(file);
      }

      if (refused.length > 0) setNotice(refused.join('\n'));
      if (accepted.length === 0) return;

      const placeholders = accepted.map((file) => ({ key: nextKey(), file }));

      setRows((current) => [
        ...current,
        ...placeholders.map<PageRow>(({ key, file }) => ({
          kind: 'upload',
          key,
          fileName: file.name,
          byteSize: file.size,
          progress: null,
        })),
      ]);

      // Photos are downscaled before leaving (1600 px, JPEG), as the previous
      // page did; a PDF is sent as-is. The compression runs in series
      // so as not to overload memory with several mobile photos at once.
      for (const { key, file } of placeholders) {
        let prepared = file;
        try {
          prepared = await compressImageFile(file);
        } catch (_) {
          prepared = file;
        }
        filesRef.current.set(key, prepared);
        queueRef.current.push({ key, file: prepared, name: file.name });
        pump();
      }
    },
    [rows, pump],
  );

  const retryUpload = useCallback(
    (key: string) => {
      const file = filesRef.current.get(key);
      const row = rows.find((item) => item.key === key);
      if (!file || !row || row.kind !== 'upload') return;
      patchRow(key, (item) =>
        item.kind === 'upload' ? { ...item, error: undefined, progress: null } : item,
      );
      queueRef.current.push({ key, file, name: row.fileName });
      pump();
    },
    [rows, patchRow, pump],
  );

  const dismissUpload = useCallback((key: string) => {
    filesRef.current.delete(key);
    queueRef.current = queueRef.current.filter((item) => item.key !== key);
    setRows((current) => current.filter((row) => row.key !== key));
  }, []);

  // Empties the queue and cuts off the in-flight upload (draft discard).
  const cancelUploads = useCallback(() => {
    cancelledRef.current = true;
    queueRef.current = [];
    abortRef.current?.abort();
  }, []);

  // --- Pages on the server ----------------------------------------------

  const removePage = useCallback(
    async (pageId: string) => {
      const id = documentIdRef.current;
      if (!id) return;
      setBusy(true);
      try {
        await deletePage(id, pageId);
        fileNamesRef.current.delete(pageId);
        // The backend renumbers the positions: the list is read again.
        syncPages(await listPages(id));
        bump();
      } catch (err) {
        setNotice(errorText(err));
      } finally {
        setBusy(false);
      }
    },
    [syncPages, bump],
  );

  const movePage = useCallback(
    async (pageId: string, direction: -1 | 1) => {
      const id = documentIdRef.current;
      if (!id) return;

      const ordered = pages.map((page) => page.id);
      const from = ordered.indexOf(pageId);
      const to = from + direction;
      if (from < 0 || to < 0 || to >= ordered.length) return;

      const next = ordered.slice();
      [next[from], next[to]] = [next[to], next[from]];

      // Optimistic: the row moves already, and is restored if the server says no.
      const before = pages;
      syncPages(
        next.map((pid, index) => ({
          ...(pages.find((page) => page.id === pid) as DocumentPage),
          position: index + 1,
        })),
      );

      setBusy(true);
      try {
        syncPages(await reorderPages(id, next));
        bump();
      } catch (err) {
        syncPages(before);
        setNotice(errorText(err));
      } finally {
        setBusy(false);
      }
    },
    [pages, syncPages, bump],
  );

  return {
    rows,
    pages,
    qr,
    uploadsInProgress,
    busy,
    notice,
    version,
    clearNotice: () => setNotice(null),
    setNotice,
    reset,
    syncPages,
    reloadPages,
    addFiles,
    retryUpload,
    dismissUpload,
    cancelUploads,
    removePage,
    movePage,
  };
};

export type PagesController = ReturnType<typeof usePages>;
